import { createGlobalApiClient, createStaticApiClient } from '@ori-appshell/shared';
import { useToken } from '@ori/auth';
import type { Api as GlobalApi } from '@ori/global-api-client';
import type { Api as StaticApi } from '@ori/static-api-client';
import type { FC } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';

interface ApiProviderValues {
  globalApiClient: GlobalApi;
  staticApiClient: StaticApi;
}

export const ApiProviderContext = createContext<ApiProviderValues | undefined>(undefined);

export const ApiProviderContextProvider: FC = ({ children }) => {
  const { currentLanguage, tenant, globalApiUrl, staticApiUrl } = useAppConfigurationContext();
  const { formattedToken } = useToken();
  const globalApiClient = createGlobalApiClient({
    acceptLanguage: currentLanguage,
    baseUrl: globalApiUrl,
    tenant,
    token: formattedToken,
  });
  const staticApiClient = createStaticApiClient({ baseUrl: staticApiUrl });
  const value: ApiProviderValues = useMemo<ApiProviderValues>(
    () => ({
      staticApiClient,
      globalApiClient,
    }),
    [globalApiClient, staticApiClient],
  );

  return <ApiProviderContext.Provider value={value}>{children}</ApiProviderContext.Provider>;
};

export const useApiProviderContext = () => {
  const context = useContext(ApiProviderContext);
  if (context) {
    return context;
  }
  throw new Error('ApiProviderContext value must be set.');
};
